// ============================================================================
// Formato de texto de WhatsApp → nodos React.
//
// WhatsApp NO usa Markdown: usa *negrita*, _cursiva_, ~tachado~ y ```mono```.
// El bot escribe con esa sintaxis (es la correcta para el canal), pero el panel
// la mostraba cruda: el asesor veía `*Sofá Cama Eco*` mientras el cliente veía
// **Sofá Cama Eco**. El inbox debe enseñar lo MISMO que ve el cliente, o el
// asesor no puede juzgar lo que el bot está diciendo.
//
// ⚠️ Se construyen elementos React, NUNCA `dangerouslySetInnerHTML`. Este texto
// puede venir de un desconocido por WhatsApp: inyectar su HTML en el panel sería
// un agujero de seguridad servido por el propio cliente.
// ============================================================================

import type { ReactNode } from "react";

interface Marca {
  /** Carácter delimitador tal como lo escribe WhatsApp. */
  signo: string;
  envolver: (hijos: ReactNode, clave: string) => ReactNode;
}

// El orden importa: ```mono``` se resuelve antes que *negrita* para que un
// bloque de código con asteriscos dentro no se parta.
const MARCAS: Marca[] = [
  { signo: "```", envolver: (h, k) => <code key={k} className="cifra text-[13px] px-1 rounded bg-[var(--color-borde-suave)]">{h}</code> },
  { signo: "*", envolver: (h, k) => <strong key={k} className="font-semibold">{h}</strong> },
  { signo: "_", envolver: (h, k) => <em key={k}>{h}</em> },
  { signo: "~", envolver: (h, k) => <s key={k} className="opacity-70">{h}</s> },
];

/** Letra o dígito de cualquier idioma (los productos llevan tildes y ñ). */
const ALFANUM = /[\p{L}\p{N}]/u;

/**
 * Aplica una marca sobre un texto plano, recursivamente para las siguientes.
 *
 * La regla que de verdad importa es el **límite de palabra**, y salió de casos
 * reales, no de la teoría:
 *   · `son 2 * 3 * 4 unidades` → NO es negrita (multiplicación)
 *   · `mide 2*2 metros`        → NO es negrita (medidas)
 *   · `sofa_cama_eco.png`      → NO es cursiva (nombre de archivo)
 *   · `*Sofá Cama Eco*`        → SÍ es negrita
 * Un delimitador solo ABRE si lo anterior no es letra ni número, y solo CIERRA
 * si lo siguiente tampoco lo es. Además el contenido no puede empezar por
 * espacio ni estar vacío. Es como se comporta WhatsApp.
 */
function aplicar(texto: string, nivel: number, prefijoClave: string): ReactNode[] {
  if (nivel >= MARCAS.length) return [texto];
  const { signo, envolver } = MARCAS[nivel];
  const partes: ReactNode[] = [];
  let i = 0;
  let cursor = 0;   // desde dónde queda texto pendiente de emitir
  let busca = 0;    // desde dónde buscar la próxima apertura

  const emitir = (hasta: number) => {
    if (hasta > cursor) {
      partes.push(...aplicar(texto.slice(cursor, hasta), nivel + 1, `${prefijoClave}-${i++}`));
      cursor = hasta;
    }
  };

  while (busca < texto.length) {
    const ini = texto.indexOf(signo, busca);
    if (ini === -1) break;
    const desde = ini + signo.length;

    // ¿puede ABRIR aquí? lo de antes no puede ser letra/número
    const antes = ini === 0 ? "" : texto[ini - 1];
    if (antes && ALFANUM.test(antes)) { busca = desde; continue; }
    // contenido no vacío y que no arranque con espacio
    if (desde >= texto.length || /\s/.test(texto[desde])) { busca = desde; continue; }

    // buscar un cierre válido: lo de después no puede ser letra/número
    let fin = -1;
    for (let p = texto.indexOf(signo, desde); p !== -1; p = texto.indexOf(signo, p + 1)) {
      if (p === desde) continue;                       // vacío
      const despues = texto[p + signo.length];
      if (despues && ALFANUM.test(despues)) continue;  // pegado a una palabra
      fin = p;
      break;
    }
    if (fin === -1) { busca = desde; continue; }       // nunca cierra → literal

    emitir(ini);
    const dentro = texto.slice(desde, fin);
    partes.push(envolver(aplicar(dentro, nivel + 1, `${prefijoClave}-${i}d`), `${prefijoClave}-${i++}`));
    cursor = fin + signo.length;
    busca = cursor;
  }

  emitir(texto.length);
  return partes;
}

/** Convierte el texto de un mensaje de WhatsApp en nodos con su formato. */
export function formatoWhatsApp(texto: string): ReactNode[] {
  return aplicar(texto, 0, "f");
}
